import { useEffect, useRef } from "react";
import type { AlertLevel, ViewscreenMode } from "../../types/game";
import type { SceneCommandExecutor } from "../../systems/SceneCommandExecutor";
import { Viewport3D } from "../viewport/Viewport3D";
import { Viewscreen } from "../Viewscreen";
import { StarChart } from "../StarChart";
import { ViewscreenLabel } from "../viewscreen/ViewscreenLabel";
import { ScanReadout } from "../viewscreen/ScanReadout";
import { SurfaceMap } from "../viewscreen/SurfaceMap";
import { Recording } from "../viewscreen/Recording";

interface Props {
  alertLevel: AlertLevel;
  executor: SceneCommandExecutor | null;
  onReady: () => void;
  viewscreenMode: ViewscreenMode | null;
  viewscreenImage: string | null;
  acceptHail: () => void;
  declineHail: () => void;
  encounterState: any;
  onDismissOverlay?: () => void;
}

// Overlays that should auto-dismiss after a short delay so they don't hide the action.
// `hail` and `pending_hail` are excluded — those are conversational and need to stay.
// `chart` is excluded — it's a navigation tool.
const AUTO_DISMISS_MS: Partial<Record<ViewscreenMode["mode"], number>> = {
  scan_readout: 10_000,
  surface_map: 12_000,
  recording: 12_000,
  magnify: 8_000,
  tactical: 12_000,
};

// Modes that render as a PiP panel rather than full-screen overlay.
// Hail is intentionally full-screen (face-to-face conversation).
const PIP_MODES = new Set<ViewscreenMode["mode"]>([
  "scan_readout",
  "surface_map",
  "recording",
  "magnify",
  "tactical",
]);

export function ViewscreenContainer({
  alertLevel,
  executor,
  onReady,
  viewscreenMode,
  viewscreenImage,
  acceptHail,
  declineHail,
  encounterState,
  onDismissOverlay,
}: Props) {
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss timer for non-hail overlays
  useEffect(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    const mode = viewscreenMode?.mode;
    if (!mode) return;
    const delay = AUTO_DISMISS_MS[mode];
    if (!delay || !onDismissOverlay) return;
    dismissTimerRef.current = setTimeout(() => onDismissOverlay(), delay);
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [viewscreenMode?.mode, viewscreenMode, onDismissOverlay]);

  const mode = viewscreenMode?.mode ?? "space";
  const overlayClass = PIP_MODES.has(mode)
    ? "viewscreen-overlay-wrap viewscreen-overlay-pip"
    : "viewscreen-overlay-wrap";

  const showDismiss = !!onDismissOverlay && (PIP_MODES.has(mode) || mode === "scan_readout");

  return (
    <div className="viewscreen-container" data-overlay-mode={mode}>
      <Viewport3D alertLevel={alertLevel} executor={executor} onReady={onReady} />

      {/* Hail portrait overlay — full-screen, conversational */}
      {viewscreenMode?.mode === "hail" && (
        <div className="viewscreen-overlay-wrap">
          <Viewscreen mode={viewscreenMode} imageUrl={viewscreenImage} />
          {onDismissOverlay && (
            <button className="viewscreen-dismiss-btn" onClick={onDismissOverlay} title="End hail">
              END HAIL
            </button>
          )}
        </div>
      )}

      {/* Incoming hail banner */}
      {viewscreenMode?.mode === "pending_hail" && (
        <div className="incoming-hail-banner">
          <div className="incoming-hail-pulse" />
          <span className="incoming-hail-text">INCOMING TRANSMISSION</span>
          <div className="incoming-hail-actions">
            <button className="incoming-hail-btn accept" onClick={acceptHail}>ACCEPT</button>
            <button className="incoming-hail-btn decline" onClick={declineHail}>DECLINE</button>
          </div>
        </div>
      )}

      {/* Scan readout — PiP overlay */}
      {viewscreenMode?.mode === "scan_readout" && (
        <div className={overlayClass}>
          <ScanReadout targetId={viewscreenMode.targetId} scanData={viewscreenMode.scanData} />
          {showDismiss && (
            <button className="viewscreen-dismiss-btn" onClick={onDismissOverlay}>CLOSE</button>
          )}
        </div>
      )}

      {/* Surface map — PiP overlay */}
      {viewscreenMode?.mode === "surface_map" && (
        <div className={overlayClass}>
          <SurfaceMap planetId={viewscreenMode.planetId} markers={viewscreenMode.markers} />
          {showDismiss && (
            <button className="viewscreen-dismiss-btn" onClick={onDismissOverlay}>CLOSE</button>
          )}
        </div>
      )}

      {/* Star chart — full-screen (navigation tool) */}
      {viewscreenMode?.mode === "chart" && (
        <div className="viewscreen-overlay-wrap">
          <StarChart
            system={encounterState?.systemChart}
            galaxy={encounterState?.galaxyChart}
            executor={executor}
            currentSystemId={encounterState?.systemChart?.id}
          />
          {onDismissOverlay && (
            <button className="viewscreen-dismiss-btn" onClick={onDismissOverlay}>CLOSE</button>
          )}
        </div>
      )}

      {/* Recording — PiP overlay */}
      {viewscreenMode?.mode === "recording" && (
        <div className={overlayClass}>
          <Recording source={viewscreenMode.source} />
          {showDismiss && (
            <button className="viewscreen-dismiss-btn" onClick={onDismissOverlay}>CLOSE</button>
          )}
        </div>
      )}

      {/* Mode label for camera-based modes (tactical, magnify) */}
      {mode !== "space" && mode !== "hail" && mode !== "pending_hail" && viewscreenMode && (
        <ViewscreenLabel mode={mode} />
      )}
    </div>
  );
}
